import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import StorageService from './storage.service';

/**
 * User information stored in localStorage
 */
export interface UserInfo {
  id: number;
  email: string; // User email for logging/display
  fullname?: string;
  roles?: string[]; // User roles for RBAC
  avatar?: string | null; // User avatar URL
  companyId?: number; // User's company ID
}

@Injectable({
  providedIn: 'root',
})
export class AuthStateService {
  private readonly authKey = 'authenticated';
  private readonly userInfoKey = 'userInfo';

  // Reactive state for avatar
  private readonly avatarSubject = new BehaviorSubject<string | null>(
    this.storage.getItem<UserInfo>(this.userInfoKey)?.avatar || null,
  );
  public avatar$ = this.avatarSubject.asObservable();

  // Reactive state for user info
  private readonly userInfoSubject = new BehaviorSubject<UserInfo | null>(
    this.storage.getItem<UserInfo>(this.userInfoKey),
  );
  public currentUser$ = this.userInfoSubject.asObservable();

  // Multi-company support: Available companies for current user
  private readonly availableCompaniesSubject = new BehaviorSubject<
    Array<{ id: number; name: string; isDefault: boolean }>
  >(this.storage.getItem('availableCompanies') || []);
  public availableCompanies$ = this.availableCompaniesSubject.asObservable();

  constructor(private readonly storage: StorageService) {
    // Observable setup
  }

  /**
   * Check if user is authenticated
   * @returns True if authenticated, false otherwise
   */
  isAuthenticated(): boolean {
    const authStatus = this.storage.getItem<string>(this.authKey);
    return authStatus === 'true';
  }

  /**
   * Set authentication status
   * @param value - Authentication status
   */
  setAuthenticated(value: boolean): void {
    this.storage.setItem(this.authKey, value ? 'true' : 'false');
  }

  /**
   * Get stored user information
   * @returns User info or null if not found
   */
  getUserInfo(): UserInfo | null {
    return this.userInfoSubject.value; // Return current value from subject
  }

  /**
   * Store user information
   * @param info - User information to store
   */
  setUserInfo(info: UserInfo): void {
    this.storage.setItem(this.userInfoKey, info);
    this.userInfoSubject.next(info);

    // Update avatar subject if present
    if (info.avatar !== undefined) {
      this.avatarSubject.next(info.avatar);
    }
  }

  /**
   * Update specifically the avatar URL
   */
  updateAvatar(url: string | null): void {
    this.avatarSubject.next(url);

    // Also update storage to persist across reloads
    const info = this.getUserInfo();
    if (info) {
      this.setUserInfo({ ...info, avatar: url });
    }
  }

  /**
   * Get currently selected company ID
   * Used for multi-company users to track which company context they're in
   */
  getCurrentCompanyId(): number | null {
    const id = this.storage.getItem<number>('currentCompanyId');
    if (id !== null && id !== undefined && !isNaN(Number(id))) {
      return Number(id);
    }
    const info = this.getUserInfo();
    if (info && info.companyId) {
      return Number(info.companyId);
    }
    const companies =
      this.storage.getItem<
        Array<{ id: number; name: string; isDefault: boolean }>
      >('availableCompanies');
    if (companies && companies.length > 0) {
      const def = companies.find((c) => c.isDefault) || companies[0];
      return Number(def.id);
    }
    return null;
  }

  /**
   * Set currently selected company ID
   * Should be called when user switches companies in the UI
   */
  setCurrentCompanyId(companyId: number | null): void {
    if (companyId === null) {
      this.storage.deleteItem('currentCompanyId');
    } else {
      this.storage.setItem('currentCompanyId', companyId);
    }
  }

  /**
   * Set available companies for the current user
   * @param companies - List of companies user belongs to
   */
  setAvailableCompanies(
    companies: Array<{ id: number; name: string; isDefault: boolean }>,
  ): void {
    this.storage.setItem('availableCompanies', companies);
    this.availableCompaniesSubject.next(companies);
  }

  /**
   * Initialize multi-company state after successful login
   * Sets available companies and selects default if none is set
   * @param companies - List of companies user belongs to
   */
  initializeCompanies(
    companies: Array<{ id: number; name: string; isDefault: boolean }>,
  ): void {
    this.setAvailableCompanies(companies);

    // Set default company as current if none set
    if (!this.getCurrentCompanyId()) {
      const defaultCompany = companies.find((c) => c.isDefault);
      if (defaultCompany) {
        this.setCurrentCompanyId(defaultCompany.id);
      } else if (companies.length > 0) {
        // If no default, use first company
        this.setCurrentCompanyId(companies[0].id);
      }
    }
  }

  /**
   * Clear all authentication state
   * Should be called on logout
   */
  clearAuthState(): void {
    this.storage.deleteItem(this.authKey);
    this.storage.deleteItem(this.userInfoKey);
    this.storage.deleteItem('currentCompanyId');
    this.storage.deleteItem('availableCompanies');
    this.storage.deleteItem('centers');
    this.avatarSubject.next(null);
    this.availableCompaniesSubject.next([]);
  }

  /**
   * Clear only user info (keep authenticated flag)
   * Useful for partial state updates
   */
  clearUserInfo(): void {
    this.storage.deleteItem(this.userInfoKey);
    this.avatarSubject.next(null);
  }
}
